import { useEffect, useId, useRef } from 'react';
import { TbAlertTriangle } from 'react-icons/tb';
import './confirmation-sheet.css';

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function ConfirmationSheet({ title, description, confirmLabel, pendingLabel, busy = false, onConfirm, onClose, error }) {
  const titleId = useId();
  const dialogRef = useRef(null);
  const openerRef = useRef(document.activeElement);
  const latestRef = useRef({ busy, onClose });
  latestRef.current = { busy, onClose };

  useEffect(() => {
    const dialog = dialogRef.current;
    const opener = openerRef.current;
    dialog?.querySelector(FOCUSABLE)?.focus();

    function handleKeyDown(event) {
      if (event.key === 'Escape' && !latestRef.current.busy) {
        event.preventDefault();
        latestRef.current.onClose();
      }
      if (event.key !== 'Tab' || !dialog) return;
      const focusable = Array.from(dialog.querySelectorAll(FOCUSABLE));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (opener?.isConnected) opener.focus();
    };
  }, []);

  return (
    <div className="gym-confirm-sheet-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onClose(); }}>
      <section ref={dialogRef} className="gym-confirm-sheet" role="alertdialog" aria-modal="true" aria-labelledby={titleId}>
        <span className="gym-confirm-sheet__handle" aria-hidden="true" />
        <span className="gym-confirm-sheet__icon" aria-hidden="true"><TbAlertTriangle /></span>
        <h2 id={titleId} className="gym-confirm-sheet__title">{title}</h2>
        {description && <p className="gym-confirm-sheet__description">{description}</p>}
        {error && <p role="alert" className="gym-form__error">{error}</p>}
        <div className="gym-confirm-sheet__actions">
          <button type="button" className="gym-button gym-button--danger" disabled={busy} onClick={onConfirm}>
            {busy ? pendingLabel : confirmLabel}
          </button>
          <button type="button" className="gym-button gym-button--secondary" disabled={busy} onClick={onClose}>Cancelar</button>
        </div>
      </section>
    </div>
  );
}
